export const SD_PASSWORD_MIN = 8;
export const SD_PASSWORD_MAX = 10;

export const SD_PASSWORD_SPECIAL_CHARS = "!@#$%^&*()_+-=[]{}|;:,.<>?";

export const SD_PASSWORD_REQUIREMENTS = [
  "8 to 10 characters long",
  "At least one UPPER CASE letter (A-Z)",
  "At least one lower case letter (a-z)",
  "At least one number (0-9)",
  `At least one special character: ${SD_PASSWORD_SPECIAL_CHARS}`,
  "No spaces at the beginning or end",
  "No more than 3 repeating characters in a row",
  "No sequential patterns longer than 3 characters",
  "Not a common password or dictionary word",
  "Cannot contain your username, email, phone, name, or date of birth",
];

const SPECIAL_RE = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/;

export function capSdPassword(value: string): string {
  return value.slice(0, SD_PASSWORD_MAX);
}

/** Live field errors — only unmet rules, matching reset-password UI. */
export function getSdPasswordLiveErrors(password: string): string[] {
  if (!password) return [];
  const errors: string[] = [];
  if (password.length < SD_PASSWORD_MIN) {
    errors.push(
      `Password must be ${SD_PASSWORD_MIN} to ${SD_PASSWORD_MAX} characters long`
    );
  }
  if (!/[A-Z]/.test(password)) {
    errors.push("Password must contain at least one UPPER CASE letter (A-Z)");
  }
  if (!/[a-z]/.test(password)) {
    errors.push("Password must contain at least one lower case letter (a-z)");
  }
  if (!/[0-9]/.test(password)) {
    errors.push("Password must contain at least one number (0-9)");
  }
  if (!SPECIAL_RE.test(password)) {
    errors.push(
      `Password must contain at least one special character (${SD_PASSWORD_SPECIAL_CHARS})`
    );
  }
  return errors;
}

function hasLongRepeat(value: string): boolean {
  return /(.)\1{3,}/.test(value);
}

function hasSequentialRun(value: string): boolean {
  const lower = value.toLowerCase();
  for (let i = 0; i <= lower.length - 4; i++) {
    const slice = lower.slice(i, i + 4);
    if (!/^[a-z0-9]{4}$/.test(slice)) continue;
    let asc = 0;
    let desc = 0;
    for (let j = 1; j < 4; j++) {
      const diff = slice.charCodeAt(j) - slice.charCodeAt(j - 1);
      if (diff === 1) asc += 1;
      if (diff === -1) desc += 1;
    }
    if (asc === 3 || desc === 3) return true;
  }
  return false;
}

export function validateSdNewPassword(password: string): string | null {
  if (!password) return "New password is required.";
  if (password !== password.trim()) {
    return "No spaces at the beginning or end.";
  }
  if (password.length < SD_PASSWORD_MIN || password.length > SD_PASSWORD_MAX) {
    return `Password must be ${SD_PASSWORD_MIN} to ${SD_PASSWORD_MAX} characters long.`;
  }
  if (!/[A-Z]/.test(password)) {
    return "Include at least one UPPER CASE letter (A-Z).";
  }
  if (!/[a-z]/.test(password)) {
    return "Include at least one lower case letter (a-z).";
  }
  if (!/[0-9]/.test(password)) {
    return "Include at least one number (0-9).";
  }
  if (!SPECIAL_RE.test(password)) {
    return `Include at least one special character: ${SD_PASSWORD_SPECIAL_CHARS}`;
  }
  if (hasLongRepeat(password)) {
    return "No more than 3 repeating characters in a row.";
  }
  if (hasSequentialRun(password)) {
    return "No sequential patterns longer than 3 characters.";
  }
  return null;
}
